import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useGlobalContext } from "../../init/GlobalContext";
import { getSubscriptionPaths, localStorageUtil } from "../../utils/functions";
import CuratedSubscription from "./steps/CuratedSubscription";
import CustomizeSubscription from "./steps/CustomizeSubscription";
import SubscriptionPeriod from "./steps/SubscriptionPeriod";
import SubscriptionSize from "./steps/SubscriptionSize";
import GiftSelection from "./steps/GiftSelection";

const CLEAN_RESET_URL_AFTER_APPLY = true;

/**
 * Subscription step flow (size → gift → customize/curated → period).
 * Used by full-page Subscription and by StaticSubscriptionPage embeds.
 */
const SubscriptionWizard = () => {
  const [, setGlobalState] = useGlobalContext();
  const location = useLocation();
  const paths = getSubscriptionPaths();
  const queryParams = new URLSearchParams(location.search);
  const parts = location.pathname.endsWith("/")
    ? location.pathname.slice(0, -1).split("/")
    : location.pathname.split("/");
  const latestPath = parts[parts.length - 1];
  const shouldReset = queryParams.get("reset") === "1";
  const resetParamsAppliedRef = useRef(false);

  const stepPathSegment = (stepPattern) => {
    const normalized = (stepPattern || "").replace(/\/$/, "");
    return normalized.split("/").pop() || "";
  };

  // Match on the last URL segment only (e.g. "cadeau"), not endsWith on the full path.
  // "/nl/ing" must not match "…/samenstelling" because that string ends with "ing".
  const matchedStep = Object.keys(paths.steps).find(
    (step) => stepPathSegment(paths.steps[step]) === latestPath
  );

  const normalizedPathname = location.pathname.replace(/\/$/, "") || "/";
  const isBasePath =
    paths.base && normalizedPathname === paths.base.replace(/\/$/, "");

  useLayoutEffect(() => {
    if (!shouldReset || resetParamsAppliedRef.current) {
      return;
    }
    resetParamsAppliedRef.current = true;

    const type = queryParams.get("type");
    const size = queryParams.get("size");
    const period = queryParams.get("period");
    const nonce = queryParams.get("nonce");

    sessionStorage.setItem("resetting_subscription", "true");
    if (nonce) {
      sessionStorage.setItem("resetting_subscription_nonce", nonce);
    }
    const giftSlug = queryParams.get("gift_slug");
    if (giftSlug) {
      sessionStorage.setItem("subscription_gift_slug", giftSlug);
    }

    const items = {};
    for (const [key, value] of queryParams.entries()) {
      if (key.startsWith("items[") && key.endsWith("]")) {
        const itemKey = key.slice(6, -1);
        items[itemKey] = value;
      }
    }

    const subscriptionData = {};
    if (type) {
      subscriptionData.type = type;
    }
    if (size) {
      subscriptionData.size = size;
    }
    if (period) {
      subscriptionData.period = period;
    }
    if (type === "curated") {
      const curated = queryParams.get("curated");
      if (curated) {
        subscriptionData.curated = curated;
      }
    } else if (Object.keys(items).length > 0) {
      subscriptionData.prefilledItems = items;
    }

    localStorageUtil.updateObjectItem("subscription", subscriptionData);

    if (size && Object.keys(items).length > 0) {
      sessionStorage.setItem("subscription_prefilled_size", size);
    }

    if (CLEAN_RESET_URL_AFTER_APPLY) {
      try {
        const params = new URLSearchParams(location.search);
        const keysToRemove = [...params.keys()];
        if (keysToRemove.length > 0) {
          keysToRemove.forEach((key) => params.delete(key));
          const newUrl = location.pathname + (location.hash || "");
          window.history.replaceState({}, "", newUrl);
        }
      } catch (_) {
        // ignore
      }
    }
  }, [location.search, shouldReset]);

  useEffect(() => {
    setGlobalState({
      customeGlobalClasse: shouldReset ? ["subscription-reset"] : [],
    });
  }, [shouldReset, setGlobalState]);

  const renderComponentBasedOnPath = () => {
    if (isBasePath || !matchedStep) {
      return <SubscriptionSize isResetting={shouldReset} />;
    }

    switch (matchedStep) {
      case "period":
        return <SubscriptionPeriod />;
      case "gift":
        return <GiftSelection />;
      case "customize":
        return <CustomizeSubscription />;
      case "curated":
        return <CuratedSubscription />;
      default:
        return <SubscriptionSize isResetting={shouldReset} />;
    }
  };

  return <>{renderComponentBasedOnPath()}</>;
};

export default SubscriptionWizard;
